import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import semver from "semver";
import { get, isEmpty } from "lodash-es";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "features/common/redux/actions";
import useUnload from "common/useUnload";
import Prompt from "features/common/Prompt";
import { useForm, FormProvider, useWatch, Controller } from "react-hook-form";
import { Button } from "@metacrm/metacrm-material-ui/dist/Button";
import Switch from "@metacrm/metacrm-material-ui/dist/Switch";

import {
  StyledCustomizationContainer,
  StyledGeneralSettingsContainer,
  StyledSettingTitle,
  StyledDescription,
  StyledSurveySettingsContainer,
  StyledInputHint,
} from "./TicketType.styles";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import FixedButton from "features/metadesk/settings/common/FixedButton";
import TicketListContainer from "./TicketListContainer";
import TicketCreationMode from "./TicketCreationMode";
import { Loading } from "features/common";
import { useIntl } from "react-intl";
import useGetPagePermission from "hooks/useGetPagePermission";
import ScoreBtnOption from "./ScoreBtnOption";
import {
  selectSurveyConfig,
  selectWidgetModule,
  selectCommunityTicketBased,
  selectAiSupportConnected,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { DEFAULT_SURVEY_MSG } from "../SurveyConstants";
import { StyledWhiteContainer } from "../../common.styles";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import useNavigateEntity from "hooks/useNavigateEntity";

const hasDuplicateName = (arr) => {
  if (isEmpty(arr)) {
    return false;
  }
  const nameSet = new Set();

  for (const item of arr) {
    const itemName = get(item, "name");
    if (!itemName) {
      return false;
    }
    if (nameSet.has(itemName.toLowerCase())) {
      return true;
    }
    nameSet.add(item?.name?.toLowerCase());
  }

  return false;
};

const TicketType = () => {
  const { formatMessage } = useIntl();

  const formDataInit = {
    ticketList: [],
    ticketCreationMode: "form",
    surveyConfig: {
      isSurveyEnabled: false,
      surveyBtnStyle: "star",
      surveyMsg: "",
    },
  };

  const methods = useForm({
    defaultValues: formDataInit,
    mode: "onChange",
  });

  const {
    formState: { isDirty, dirtyFields },
    reset,
    getValues,
  } = methods;

  // 使用 useWatch 監聽所有表單字段
  const ticketList = useWatch({
    control: methods.control,
    name: "ticketList",
    defaultValue: [],
  });

  const surveyConfig = useWatch({
    control: methods.control,
    name: "surveyConfig",
    defaultValue: formDataInit.surveyConfig,
  });

  const dispatch = useDispatch();
  const [entityName, isCustomDomain] = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);

  const {
    ticketTypeList,
    fetchTicketTypeList,
    updateTicketTypeList,
    updateSurveyConfig,
    updateTicketCreationMode,
  } = useFetchEntityConfig();
  const selectedSurveyConfig = useSelector(selectSurveyConfig);
  const selectedWidgetModule = useSelector(selectWidgetModule);
  const communityTicketBased = useSelector(selectCommunityTicketBased);
  const aiSupportConnected = useSelector(selectAiSupportConnected);

  const confirm = useConfirm();
  const { navigateEntity } = useNavigateEntity();

  const isAnyEmptyInTicketList = () => {
    return ticketList.some((obj) => obj.name === "");
  };

  const isEmptySurveyMsg = () => {
    return surveyConfig?.isSurveyEnabled && _.isEmpty(surveyConfig.surveyMsg);
  };

  const isValid =
    !hasDuplicateName(ticketList) &&
    isDirty &&
    !isAnyEmptyInTicketList() &&
    !isEmptySurveyMsg();

  const showWarning = hasDuplicateName(ticketList) || isAnyEmptyInTicketList();

  useEffect(() => {
    if (_.isEmpty(selectedSurveyConfig) || _.isEmpty(ticketTypeList)) return;
    updateList(ticketTypeList);
  }, [ticketTypeList, selectedSurveyConfig]);

  useUnload((e) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  const updateList = (data) => {
    const dataWithDefaultDiscordInfo = data.map((item) => {
      if (
        item.addressRequired === undefined ||
        item.discordButtonStyle === undefined
      ) {
        return { ...item, discordButtonStyle: 1, addressRequired: false };
      }
      return item;
    });

    // 初始化表單數據
    const initialFormData = {
      ticketList: dataWithDefaultDiscordInfo,
      ticketCreationMode: communityTicketBased ? "form" : "chat",
      surveyConfig: selectedSurveyConfig || formDataInit.surveyConfig,
    };

    // 重置表單並設置新的默認值
    reset(initialFormData);
  };

  const getCleanSurveyMsg = ({ type, surveyConfig }) => {
    const { surveyMsg } = surveyConfig;
    switch (type) {
      case "star": {
        return surveyMsg?.length
          ? surveyMsg
          : formatMessage({ id: DEFAULT_SURVEY_MSG.star.msg });
      }
      case "emoji": {
        return surveyMsg?.length
          ? surveyMsg
          : formatMessage({ id: DEFAULT_SURVEY_MSG.emoji.msg });
      }
      default: {
        throw new Error("Invalid survey msg type provided");
      }
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const currentFormData = getValues();

      // 檢查哪些字段有變化並保存相應的數據
      if (dirtyFields.ticketList) {
        await updateTicketTypeList({
          entityName,
          ticketTypes: currentFormData.ticketList.filter(
            (ticket) => ticket.name.trim() !== ""
          ),
        });
      }

      if (dirtyFields.surveyConfig) {
        await updateSurveyConfig({
          entityName,
          surveyConfig: {
            ...currentFormData.surveyConfig,
            surveyMsg: getCleanSurveyMsg({
              type: currentFormData.surveyConfig.surveyBtnStyle,
              surveyConfig: currentFormData.surveyConfig,
            }),
          },
        });
      }

      if (dirtyFields.ticketCreationMode) {
        await updateTicketCreationMode({
          entityName,
          communityTicketBased: currentFormData.ticketCreationMode === "form",
        });
      }

      dispatch(
        enqueueSnackbar({
          message: formatMessage({ id: "global.snackbar.success" }),
          options: {
            variant: "success",
          },
        })
      );

      // 保存成功後重置 dirty 狀態
      reset(currentFormData);
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
          options: {
            variant: "error",
          },
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const pagePermission = useGetPagePermission();
  const { canEdit } = pagePermission("ticketSettings");

  // 設置 ticketList（從 TicketListContainer 調用）
  const setTicketList = () => {
    // 空函數，讓 TicketListContainer 中的 append/replace/remove 操作自己處理
    // ticketList 會通過 useWatch 自動更新
  };

  function renderTicketCreationMode() {
    return (
      <StyledGeneralSettingsContainer id="ticketCreationMode">
        <StyledSettingTitle>
          {formatMessage({
            id: "ticketSetting.creationMode.title",
          })}
        </StyledSettingTitle>
        <StyledDescription>
          {formatMessage({
            id: "ticketSetting.creationMode.description",
          })}
        </StyledDescription>
        <Controller
          name="ticketCreationMode"
          control={methods.control}
          render={({ field: { value, onChange } }) => (
            <TicketCreationMode
              ticketCreationMode={value}
              onTicketCreationModeChange={(mode) => {
                // 如果切換到 chat mode 且 AI Support 沒有連接，顯示確認對話框
                if (mode === "chat" && !aiSupportConnected) {
                  return confirm({
                    title: formatMessage({
                      id: "popup.chatModeSwitchRequired.title",
                    }),
                    content: formatMessage({
                      id: "popup.chatModeSwitchRequired.description",
                    }),
                    confirmText: formatMessage({
                      id: "global.btn.goToSettings",
                    }),
                    cancelText: formatMessage({ id: "global.btn.doItLater" }),
                    color: "secondary",
                    cancelColor: "secondary",
                  })
                    .then(() => {
                      navigateEntity("/settings/ai/tools");
                    })
                    .catch(() => {});
                }
                // 正常設置模式
                onChange(mode);
              }}
              canEdit={canEdit}
            />
          )}
        />
      </StyledGeneralSettingsContainer>
    );
  }

  function renderTicketTypeList() {
    return (
      <StyledGeneralSettingsContainer id="ticketTypeGuide">
        <StyledSettingTitle>
          {formatMessage({
            id: "ticketCustomization.ticketType.subtitle",
          })}
        </StyledSettingTitle>
        <StyledDescription>
          {formatMessage({
            id: "ticketCustomization.ticketType.description",
          })}
        </StyledDescription>
        <TicketListContainer
          ticketList={ticketList}
          setTicketList={setTicketList}
          showWarning={showWarning}
        />
      </StyledGeneralSettingsContainer>
    );
  }

  function renderSurveySetting() {
    const widgetVersion = _.get(selectedWidgetModule, "version");
    const MIN_WIDGET_VERSION_WITH_SURVEY_FEATURE = "1.2.0";

    if (
      widgetVersion &&
      semver.lt(widgetVersion, MIN_WIDGET_VERSION_WITH_SURVEY_FEATURE)
    ) {
      return false;
    }

    return (
      <StyledSurveySettingsContainer>
        <Controller
          name="surveyConfig"
          control={methods.control}
          render={({ field: { value, onChange } }) => (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <StyledSettingTitle>
                    {formatMessage({
                      id: "ticketCustomization.csatSetting.title",
                    })}
                  </StyledSettingTitle>
                  <StyledDescription>
                    {formatMessage({
                      id: "ticketCustomization.csatSetting.description",
                    })}
                  </StyledDescription>
                </Box>
                {canEdit && (
                  <Box>
                    <Switch
                      sx={{
                        display: "flex",
                        columnGap: 14,
                      }}
                      label={[
                        formatMessage({ id: "global.status.disabled" }),
                        formatMessage({ id: "global.status.enabled" }),
                      ]}
                      labelPlacement="start"
                      checked={value.isSurveyEnabled}
                      onChange={() => {
                        if (!value.isSurveyEnabled) {
                          // disable -> enable: 檢查 surveyMsg 是否為空，空的話套入默認值
                          onChange({
                            ...value,
                            isSurveyEnabled: true,
                            surveyMsg:
                              value.surveyMsg === ""
                                ? formatMessage({
                                    id: DEFAULT_SURVEY_MSG.star.msg,
                                  })
                                : value.surveyMsg,
                          });
                        } else {
                          // enable -> disable: 恢復到 selectedSurveyConfig 的樣子
                          onChange({
                            ...selectedSurveyConfig,
                            isSurveyEnabled: false,
                          });
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
              {value.isSurveyEnabled && (
                <>
                  <ScoreBtnOption
                    surveyConfig={value}
                    selectedSurveyConfig={selectedSurveyConfig}
                    handleChangeSurveyConfig={({ key, val }) => {
                      const currentSurveyConfig =
                        methods.getValues("surveyConfig");
                      console.log("currentSurveyConfig", currentSurveyConfig);
                      const newValue = {
                        ...currentSurveyConfig,
                        [key]: val,
                      };
                      console.log("newValue", newValue);
                      onChange(newValue);
                    }}
                  />
                  <StyledInputHint>
                    <Box mr="3px" mt="1px" display="inline">
                      <i className="meta-crm-icon-ic_info font-size-14" />
                    </Box>
                    {formatMessage({
                      id: "ticketCustomization.satisfactionSurveySetting.explanatory",
                    })}
                  </StyledInputHint>
                </>
              )}
            </>
          )}
        />
      </StyledSurveySettingsContainer>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box mb="20px" className="fade-up">
        <Box className="title">
          {formatMessage({ id: "global.function.ticketSetting" })}
        </Box>
        <Box className="description">
          {formatMessage({ id: "ticketSetting.header.description" })}
        </Box>
      </Box>
      <StyledWhiteContainer className="fade-up-more">
        <StyledCustomizationContainer>
          <Loading open={isLoading} fullScreen={false} />
          {renderTicketCreationMode()}
          {renderTicketTypeList()}
          {renderSurveySetting()}
          {canEdit && (
            <FixedButton>
              <Button
                sx={{ width: "120px", marginLeft: "8px" }}
                onClick={handleSave}
                disabled={!isValid}
              >
                {formatMessage({ id: "global.btn.save" })}
              </Button>
            </FixedButton>
          )}
          <Prompt when={isDirty} />
        </StyledCustomizationContainer>
      </StyledWhiteContainer>
    </FormProvider>
  );
};

export default TicketType;
